import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import type { SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import type { FirestoreAssignmentRubric } from '@breakoutlearning/firebase-repository/models/AssignmentRubric'
import { RubricType } from '@breakoutlearning/firebase-repository/types'
import type { useTranslationTyped } from 'i18n/i18n'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Typed `instructor_library` keys used by the custom-rubrics UI (consumed via
 * `tt.instructor_library[key]()`). */
type CustomRubricI18nKey = Extract<
  keyof ReturnType<typeof useTranslationTyped>['tt']['instructor_library'],
  `custom_rubrics_${string}`
>

// grid[rubricId][columnKey] = percentage (0–100)
export type WeightGrid = Record<string, Record<string, number>>
export type WeightColumn = { key: string; label: string }

/** Sentinel column key used when the deck has no roles (single allRoles column). */
export const ALL_ROLES_COLUMN = '__all__'

type RubricWeights = FirestoreAssignmentRubric['weights']

export type RubricConfigPayload = {
  rubricId: string
  weights: RubricWeights
  scoreMapping: Record<string, number>
}

/** Shape written into the ref shared with the dialog's save handler. */
export type CustomRubricsConfig = {
  enabled: boolean
  isValid: boolean
  rubricConfigs: RubricConfigPayload[]
}

// A score level within a scale (rendered as a coloured badge + label).
export type ScaleLevel = {
  score: number
  labelKey: CustomRubricI18nKey
}

export type ScaleId = 'dqr' | 'blooms' | 'passFail'

export type ScaleDef = {
  id: ScaleId
  labelKey: CustomRubricI18nKey
  rubricTypes: RubricType[]
  /** highest score first */
  levels: ScaleLevel[]
  defaultMapping: Record<string, number>
}

/**
 * A user-level saved score-mapping default: the mapping to seed a rubric of a
 * given type with. Stored under the user's profile and keyed by rubric type
 * (matches the firebase-repository `FirestoreScoreMappingDefault`).
 */
export type ScoreMappingDefaultEntry = {
  rubricType: number
  scoreMapping: Record<string, number>
}

// ---------------------------------------------------------------------------
// Scales — every rubric type belongs to exactly one scale. All are configurable.
// ---------------------------------------------------------------------------

export const SCALES: ScaleDef[] = [
  {
    id: 'dqr',
    labelKey: 'custom_rubrics_scale_dqr',
    rubricTypes: [
      RubricType.evidence,
      RubricType.peerEngagement,
      RubricType.criticalThinking,
      RubricType.decisionMaking,
    ],
    levels: [
      { score: 4, labelKey: 'custom_rubrics_level_dqr_4' },
      { score: 3, labelKey: 'custom_rubrics_level_dqr_3' },
      { score: 2, labelKey: 'custom_rubrics_level_dqr_2' },
      { score: 1, labelKey: 'custom_rubrics_level_dqr_1' },
      { score: 0, labelKey: 'custom_rubrics_level_dqr_0' },
    ],
    defaultMapping: { '4': 100, '3': 75, '2': 50, '1': 25, '0': 0 },
  },
  {
    id: 'blooms',
    labelKey: 'custom_rubrics_scale_blooms',
    rubricTypes: [RubricType.blooms],
    levels: [
      { score: 6, labelKey: 'custom_rubrics_level_blooms_6' },
      { score: 5, labelKey: 'custom_rubrics_level_blooms_5' },
      { score: 4, labelKey: 'custom_rubrics_level_blooms_4' },
      { score: 3, labelKey: 'custom_rubrics_level_blooms_3' },
      { score: 2, labelKey: 'custom_rubrics_level_blooms_2' },
      { score: 1, labelKey: 'custom_rubrics_level_blooms_1' },
      { score: 0, labelKey: 'custom_rubrics_level_blooms_0' },
    ],
    // Blooms currently gives full credit for any non-zero score.
    defaultMapping: {
      '6': 100,
      '5': 100,
      '4': 100,
      '3': 100,
      '2': 100,
      '1': 100,
      '0': 0,
    },
  },
  {
    id: 'passFail',
    labelKey: 'custom_rubrics_scale_pass_fail',
    rubricTypes: [RubricType.passFail],
    levels: [
      { score: 1, labelKey: 'custom_rubrics_level_pass_fail_1' },
      { score: 0, labelKey: 'custom_rubrics_level_pass_fail_0' },
    ],
    defaultMapping: { '1': 100, '0': 0 },
  },
]

export function scaleForRubricType(type: number): ScaleDef | undefined {
  return SCALES.find((scale) => scale.rubricTypes.includes(type))
}

/**
 * Flattens the current per-scale score mappings into the per-rubric-type
 * defaults payload. A scale shares one mapping across all its rubric types, so
 * each of those types gets the same mapping — this lets a saved default later
 * apply to any rubric of a type in that scale.
 */
export function buildScoreMappingDefaults(
  scales: ScaleDef[],
  mappings: Record<ScaleId, Record<string, number>>
): ScoreMappingDefaultEntry[] {
  return scales.flatMap((scale) =>
    scale.rubricTypes.map((rubricType) => ({
      rubricType,
      scoreMapping: mappings[scale.id],
    }))
  )
}

/**
 * The saved default mapping for a scale, matched by any of the scale's rubric
 * types. Returns undefined when the user has no saved default for the scale.
 */
export function defaultMappingForScale(
  scale: ScaleDef,
  defaults: ScoreMappingDefaultEntry[]
): Record<string, number> | undefined {
  return defaults.find((entry) => scale.rubricTypes.includes(entry.rubricType))
    ?.scoreMapping
}

/**
 * Every rubric whose type maps to a known scale can be customised. Student
 * rubric types all map to a scale; `demoDelivery` deliberately does not, so
 * internal demo-evaluation rubrics stay out of the instructor grading UI.
 */
export function isRubricEligibleForCustomGrading(rubric: SlideRubric): boolean {
  return scaleForRubricType(rubric.data.rubricType) !== undefined
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Role ids a rubric applies to. A rubric with a non-empty `assignmentRoles` is
 * role-specific (applies only to those roles); an empty/undefined
 * `assignmentRoles` applies to every role. Result is intersected with the deck's
 * current roles. If a role-specific rubric references no current role (stale
 * data), it falls back to all roles.
 */
export function applicableRoleIdsForRubric(
  rubric: SlideRubric,
  roles: SlideDeckRole[]
): string[] {
  const allRoleIds = roles.map((r) => r.id)
  const assigned = rubric.data.assignmentRoles
  if (!assigned || assigned.length === 0) return allRoleIds
  const intersection = allRoleIds.filter((id) => assigned.includes(id))
  return intersection.length > 0 ? intersection : allRoleIds
}

/** Whether a rubric contributes to a given weight column (role tab). The
 * single all-roles column (deck has no roles) always applies. */
export function rubricAppliesToColumn(
  rubric: SlideRubric,
  columnKey: string,
  roles: SlideDeckRole[]
): boolean {
  if (columnKey === ALL_ROLES_COLUMN) return true
  return applicableRoleIdsForRubric(rubric, roles).includes(columnKey)
}

/** Sum of a column's weights over only the rubrics applicable to that column. */
export function columnSum(
  grid: WeightGrid,
  columnKey: string,
  rubrics: SlideRubric[],
  roles: SlideDeckRole[]
): number {
  return rubrics.reduce(
    (sum, rubric) =>
      rubricAppliesToColumn(rubric, columnKey, roles)
        ? sum + (grid[rubric.id]?.[columnKey] ?? 0)
        : sum,
    0
  )
}

export function isWeightGridValid(
  grid: WeightGrid,
  columns: WeightColumn[],
  rubrics: SlideRubric[],
  roles: SlideDeckRole[]
): boolean {
  return columns.every((c) => columnSum(grid, c.key, rubrics, roles) === 100)
}

/**
 * A score mapping is valid when it is non-strictly descending over the scale's
 * levels: a higher score must map to a percentage >= every lower score's.
 */
export function isScoreMappingDescending(
  mapping: Record<string, number>,
  levels: ScaleLevel[]
): boolean {
  let previous = Number.POSITIVE_INFINITY
  for (const level of levels) {
    const value = mapping[String(level.score)] ?? 0
    if (value > previous) return false
    previous = value
  }
  return true
}

/** Distribute `total` across `n` buckets as integers that sum to `total`. */
export function equalSplit(total: number, n: number): number[] {
  if (n <= 0) return []
  const base = Math.floor(total / n)
  const remainder = total - base * n
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0))
}
